(() => {
  const ARTICLE_IMAGE_SELECTOR = ".article-content img, .article-header__image img";
  const MIN_RENDERED_WIDTH = 240;
  const MIN_RENDERED_HEIGHT = 160;
  const MIN_ZOOM_SCALE = 1.35;
  const MIN_EXTRA_WIDTH = 160;
  const MIN_EXTRA_VIEWPORT_SIZE = 80;
  const EXCLUDED_SELECTOR = [
    "a",
    "image-zoom",
    "[data-article-image-zoom]",
    "[data-article-component]",
    ".no-zoom",
    "[data-no-zoom]",
    ".article-toc",
    ".tablecontents",
    ".faq-metaobject",
    ".article-related",
    ".article-related-products",
    ".main-article-download",
    ".blog-author-block",
    ".gravatar",
    ".comment",
    ".share",
  ].join(",");

  const ZOOM_ICON = `
    <svg
      aria-hidden="true"
      focusable="false"
      role="presentation"
      class="svg symbol symbol--zoom-in"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
    >
      <path d="M10.2 19.2a8.96 8.96 0 0 1-9-9 8.96 8.96 0 0 1 9-9 8.96 8.96 0 0 1 9 9 8.96 8.96 0 0 1-9 9zm0-16c-3.9 0-7 3.1-7 7s3.1 7 7 7 7-3.1 7-7c0-3.8-3.1-7-7-7z"/><path d="M15.65 17.1l1.414-1.414 5.657 5.657-1.414 1.414zM14.3 9.3h-3v-3h-2v3h-3v2h3v3h2v-3h3z"/>
    </svg>
  `;

  function getArticleImages(root) {
    const images = [];

    if (
      root.nodeType === Node.ELEMENT_NODE &&
      root.matches(ARTICLE_IMAGE_SELECTOR)
    ) {
      images.push(root);
    }

    images.push(...root.querySelectorAll(ARTICLE_IMAGE_SELECTOR));

    return [...new Set(images)];
  }

  function getLargestSrcsetCandidate(srcset) {
    if (!srcset) return null;

    return srcset
      .split(",")
      .map((candidate) => {
        const [url, descriptor = ""] = candidate.trim().split(/\s+/);
        const width = descriptor.endsWith("w")
          ? Number.parseInt(descriptor, 10) || 0
          : 0;

        return { url, width };
      })
      .filter((candidate) => candidate.url)
      .sort((first, second) => second.width - first.width)[0];
  }

  function getZoomImageUrl(image) {
    const srcsetCandidate = getLargestSrcsetCandidate(image.getAttribute("srcset"));
    const dataSrcsetCandidate = getLargestSrcsetCandidate(
      image.getAttribute("data-srcset"),
    );
    const source =
      (srcsetCandidate && srcsetCandidate.url) ||
      (dataSrcsetCandidate && dataSrcsetCandidate.url) ||
      image.currentSrc ||
      image.getAttribute("data-src") ||
      image.getAttribute("src") ||
      "";

    return source.replace(
      /(_(?:pico|icon|thumb|small|compact|medium|large|grande|master|original|\d+x\d*|\d*x\d+))(\.[a-zA-Z0-9]+)(\?.*)?$/,
      "$2$3",
    );
  }

  function hasUsefulZoom(image) {
    const rect = image.getBoundingClientRect();
    const renderedWidth = rect.width || image.clientWidth;
    const renderedHeight = rect.height || image.clientHeight;
    const srcsetCandidate =
      getLargestSrcsetCandidate(image.getAttribute("srcset")) ||
      getLargestSrcsetCandidate(image.getAttribute("data-srcset"));
    const widthAttribute = Number.parseInt(image.getAttribute("width"), 10) || 0;
    const heightAttribute = Number.parseInt(image.getAttribute("height"), 10) || 0;
    const aspectRatio =
      image.naturalWidth && image.naturalHeight
        ? image.naturalWidth / image.naturalHeight
        : widthAttribute / heightAttribute;
    const naturalWidth = srcsetCandidate
      ? srcsetCandidate.width
      : image.naturalWidth || widthAttribute || 0;
    const naturalHeight =
      srcsetCandidate && srcsetCandidate.width && aspectRatio
        ? srcsetCandidate.width / aspectRatio
        : image.naturalHeight || heightAttribute || 0;

    if (
      renderedWidth < MIN_RENDERED_WIDTH ||
      renderedHeight < MIN_RENDERED_HEIGHT ||
      naturalWidth <= 0 ||
      naturalHeight <= 0
    ) {
      return false;
    }

    const hasRenderedSizeZoom =
      naturalWidth >= renderedWidth * MIN_ZOOM_SCALE &&
      naturalHeight >= renderedHeight * MIN_ZOOM_SCALE &&
      naturalWidth - renderedWidth >= MIN_EXTRA_WIDTH;

    const hasViewportSizeZoom =
      (naturalWidth > window.innerWidth || naturalHeight > window.innerHeight) &&
      (naturalWidth - renderedWidth >= MIN_EXTRA_VIEWPORT_SIZE ||
        naturalHeight - renderedHeight >= MIN_EXTRA_VIEWPORT_SIZE);

    return hasRenderedSizeZoom || hasViewportSizeZoom;
  }

  function shouldEnhanceImage(image) {
    const source =
      image.currentSrc ||
      image.getAttribute("data-src") ||
      image.getAttribute("src") ||
      "";

    if (!source || source.startsWith("data:") || source.includes(".svg")) {
      return false;
    }

    if (image.closest(EXCLUDED_SELECTOR)) {
      return false;
    }

    return hasUsefulZoom(image);
  }

  function createZoomElement(image) {
    const zoom = document.createElement("image-zoom");

    zoom.className = "image-zoom image-zoom--article";
    zoom.setAttribute("data-image", getZoomImageUrl(image));
    zoom.setAttribute("aria-hidden", "true");
    zoom.setAttribute("tabindex", "-1");
    zoom.innerHTML = ZOOM_ICON;

    return zoom;
  }

  function enhanceImage(image) {
    if (!shouldEnhanceImage(image)) return;

    const lazyImage = image.closest(".article-header__image .lazy-image");

    if (lazyImage) {
      lazyImage.dataset.articleImageZoom = "";
      lazyImage.appendChild(createZoomElement(image));
      return;
    }

    const mediaElement =
      image.parentElement && image.parentElement.tagName === "PICTURE"
        ? image.parentElement
        : image;
    const wrapper = document.createElement("span");

    wrapper.className = "article-content__image-zoom-wrapper";
    wrapper.dataset.articleImageZoom = "";

    mediaElement.parentNode.insertBefore(wrapper, mediaElement);
    wrapper.appendChild(mediaElement);

    wrapper.appendChild(createZoomElement(image));
  }

  function enhanceImageWhenReady(image) {
    if (image.complete && image.naturalWidth > 0) {
      enhanceImage(image);
      return;
    }

    image.addEventListener("load", () => enhanceImage(image), { once: true });
  }

  function initArticleImageZoom(root = document) {
    getArticleImages(root).forEach(enhanceImageWhenReady);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initArticleImageZoom());
  } else {
    initArticleImageZoom();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initArticleImageZoom(event.target);
  });
})();
